import React from 'react';
import { SkeletonItem } from '@atlaskit/side-navigation';
import { SOFTWARE_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { useBoard as useBoardDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/board/index.tsx';
import { useProject as useProjectDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { withThrowOnError } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/throw-when-no-data-and-error/index.tsx';
import { matchUrlPathname } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import { useFeatures as useFeaturesDI } from '@atlassian/jira-navigation-apps-sidebar-next-gen-use-features/src/controllers/features/index.tsx';
import { messages } from './messages.tsx';

export const useProject = withThrowOnError(useProjectDI);
export const useBoard = withThrowOnError(useBoardDI);
export const useFeatures = withThrowOnError(useFeaturesDI);

const genBoardSettingsUrl = (projectKey: string, boardId: number): string =>
	`/jira/${SOFTWARE_PROJECT}/projects/${projectKey}/settings/boards/${boardId}`;

export const BoardMenu = () => {
	const { formatMessage } = useIntl();
	const { data: project, loading: projectLoading } = useProject();
	const { loading: boardLoading, data: board } = useBoard();
	const { data: features } = useFeatures();

	if (projectLoading || boardLoading || !board || !project) return <SkeletonItem />;

	const boardSettingsUrl = genBoardSettingsUrl(project.key, board.id);

	const content = (
		<>
			<MenuItem
				analytics={{ itemId: 'board-columns' }}
				href={`${boardSettingsUrl}/columns`}
				selectedOn={matchUrlPathname()}
			>
				{formatMessage(messages.itemBoardColumns)}
			</MenuItem>
			<MenuItem
				analytics={{ itemId: 'board-custom-filters' }}
				href={`${boardSettingsUrl}/custom-filters`}
				selectedOn={matchUrlPathname()}
			>
				{formatMessage(messages.itemBoardCustomFilters)}
			</MenuItem>
			<MenuItem
				analytics={{ itemId: 'board-card' }}
				href={`${boardSettingsUrl}/card`}
				selectedOn={matchUrlPathname()}
			>
				{formatMessage(messages.itemBoardCard)}
			</MenuItem>
			{features?.hasRoadmap && (
				<MenuItem
					analytics={{ itemId: 'board-roadmap' }}
					href={`${boardSettingsUrl}/timeline`}
					selectedOn={matchUrlPathname()}
				>
					{formatMessage(messages.itemBoardTimeline)}
				</MenuItem>
			)}
		</>
	);

	return <>{content}</>;
};
